import * as React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Header from '../components/layout/Header';
import LawFirmsSection from '../components/sections/LawFirmsSection';
import WhyLawFirmsSection from '../components/sections/WhyLawFirmsSection';
import ContactSection from '../components/sections/ContactSection';
import Footer from '../components/layout/Footer';

const LawFirms = () => {
  // Create refs for scrolling to sections
  const firmsRef = React.useRef<HTMLDivElement>(null);
  const whyRef = React.useRef<HTMLDivElement>(null);
  const transitionRef = React.useRef<HTMLDivElement>(null);
  const contactRef = React.useRef<HTMLDivElement>(null);

  const scrollToSection = (
    elementRef: React.RefObject<HTMLDivElement | null>,
  ) => {
    if (elementRef.current) {
      elementRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header/Navigation */}
      <Header scrollToSection={scrollToSection} contactRef={contactRef} />

      {/* Main content sections */}
      <LawFirmsSection
        ref={firmsRef}
        scrollToContact={() => scrollToSection(contactRef)}
      />

      {/* Scroll indicator */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: -6, // Negative margin to overlap with the bottom of section
          mb: 2, // Margin before the gradient starts
          position: 'relative',
          zIndex: 2, // Higher than content but lower than fixed elements
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1,
            fontWeight: 500,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          Learn More
        </Typography>
        <IconButton
          color="primary"
          onClick={() => scrollToSection(transitionRef)}
          sx={{
            backgroundColor: 'white',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            '&:hover': {
              backgroundColor: 'white',
              transform: 'translateY(4px)',
            },
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'translateY(0px)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              },
              '50%': {
                transform: 'translateY(5px)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
              },
              '100%': {
                transform: 'translateY(0px)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              },
            },
          }}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      </Box>

      <Box
        ref={transitionRef}
        sx={{
          height: '40px',
          background:
            'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(248,248,252,1) 100%)',
          position: 'relative',
          zIndex: 1,
          scrollMarginTop: '-8px',
        }}
      />

      {/* "Why Law Firms Use ClassAct" section */}
      <WhyLawFirmsSection
        ref={whyRef}
        scrollToContact={() => scrollToSection(contactRef)}
      />

      <Box
        sx={{
          height: '20px',
          background:
            'linear-gradient(to bottom, rgba(248,248,252,1) 0%, rgba(255,255,255,1) 100%)',
          position: 'relative',
          zIndex: 1,
        }}
      />

      {/* Contact section */}
      <ContactSection ref={contactRef} />

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default LawFirms;
